import React from "react";
import {
  useGetAdminStatsQuery,
  useGetAdminListQuery,
} from "../../../../store/slices/api";
import { SharedLoading, SharedFeatureError } from "../../../shared";

function StatCard({ label, value }) {
  return (
    <div className="flex flex-col items-center justify-center bg-white dark:bg-darkCard rounded-[16px] shadow-md p-[24px] border border-dupontGold/30 dark:border-darkGold/20">
      <div className="text-[32px] font-bold text-dupontBlue dark:text-darkText">{value ?? "\u2014"}</div>
      <div className="text-[14px] text-dupontGray dark:text-darkTextSecondary mt-[4px]">{label}</div>
    </div>
  );
}

function Overview({ individualId }) {
  const {
    data: stats,
    isLoading: isLoadingStats,
    isFetching: isFetchingStats,
    isError: statsFailed,
    refetch: refetchStats,
  } = useGetAdminStatsQuery(individualId);

  const {
    data: admins,
    isLoading: isLoadingAdmins,
    isFetching: isFetchingAdmins,
    isError: adminsFailed,
    refetch: refetchAdmins,
  } = useGetAdminListQuery(individualId);

  // Also while "Try again" runs: RTK Query then reports neither loading nor
  // error and no data, which would flash dashes and "No admins found".
  if (
    isLoadingStats ||
    isLoadingAdmins ||
    (stats === undefined && isFetchingStats) ||
    (admins === undefined && isFetchingAdmins)
  ) {
    return <SharedLoading />;
  }

  // A failed load would otherwise show a dash in every box and "No admins
  // found", which reads as real numbers. Say it failed, with a retry.
  if ((statsFailed && !stats) || (adminsFailed && !admins)) {
    return (
      <SharedFeatureError
        title="We couldn't load the overview"
        onRetry={() => {
          if (statsFailed) refetchStats();
          if (adminsFailed) refetchAdmins();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-y-[30px]">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[16px]">
        <StatCard label="Individuals" value={stats?.individuals} />
        <StatCard label="Families" value={stats?.families} />
        <StatCard label="Bulletin Posts" value={stats?.bulletin_posts} />
        <StatCard label="Marketplace Items" value={stats?.marketplace_items} />
        <StatCard label="Connected Users" value={stats?.connected_users} />
        <StatCard label="Conversations" value={stats?.conversations} />
        <StatCard label="Messages" value={stats?.messages} />
        <StatCard label="Surname Branches" value={stats?.surname_branches} />
      </div>

      {/* Admin List */}
      <div className="bg-white dark:bg-darkCard rounded-[16px] shadow-md p-[24px] border border-dupontGold/30 dark:border-darkGold/20">
        <div className="text-[18px] font-bold text-dupontBlue dark:text-darkText mb-[16px]">
          Current Admins
        </div>
        {admins && admins.length > 0 ? (
          <div className="flex flex-col gap-y-[8px]">
            {admins.map((admin) => (
              <div
                key={admin.id}
                className="flex flex-row items-center gap-x-[12px] py-[8px] px-[12px] rounded-[8px] bg-dupontCream dark:bg-darkPrimary"
              >
                <span className="text-[11px] font-bold text-dupontGray dark:text-darkTextSecondary bg-dupontCreamDk dark:bg-darkGold/20 px-[8px] py-[2px] rounded-full">
                  Admin
                </span>
                <span className="text-[15px] text-dupontBlue dark:text-darkText font-medium">
                  {admin.name}
                </span>
                <span className="text-[13px] text-dupontGray dark:text-darkTextSecondary">
                  (ID: {admin.id})
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[14px] text-dupontGray dark:text-darkTextSecondary">No admins found.</div>
        )}
        <div className="text-[13px] text-dupontGray dark:text-darkTextSecondary mt-[16px] italic">
          To add or remove admins, use the terminal (Flask shell).
        </div>
      </div>
    </div>
  );
}

export default Overview;
